import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Button,
  Tabs,
  Tab,
  Paper,
  Link,
  Alert
} from '@mui/material';
import { Dashboard, QueryStats, Download } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { ROLES } from '../constants/roles';
import { canApprove } from '../utils/roleHierarchy';
import AircraftSelector from './AircraftLifecycleComponents/AircraftSelector';
import PartNumberSearchBar from './AircraftLifecycleComponents/PartNumberSearchBar';
import AssemblyTable from './AircraftLifecycleComponents/AssemblyTable';
import ComponentTable from './AircraftLifecycleComponents/ComponentTable';
import AssemblyFormDialog from './AircraftLifecycleComponents/AssemblyFormDialog';
import DocumentManagementDialog from './AircraftLifecycleComponents/DocumentManagementDialog';
import AircraftLifecycleDashboard from './AircraftLifecycleComponents/AircraftLifecycleDashboard';
import ComponentForm from './components/ComponentForm';
import { useAircraftLifecycleData } from './AircraftLifecycle/hooks/useAircraftLifecycleData';
import { useAssemblyHandlers } from './AircraftLifecycle/hooks/useAssemblyHandlers';
import { useComponentHandlers } from './AircraftLifecycle/hooks/useComponentHandlers';
import { useDocumentHandlers } from './AircraftLifecycle/hooks/useDocumentHandlers';
import { useCSVExport } from './AircraftLifecycle/hooks/useCSVExport';
import { getStatusColor } from './AircraftLifecycle/utils/aircraftLifecycleUtils';
import LiveAircraftUsagePanel from './AircraftFormTabs/LiveAircraftUsagePanel';
import { useToast } from '../contexts/ToastContext';
import WorkflowGuide from './common/WorkflowGuide';

const AircraftLifecycle = () => {
  const [clientSettings, setClientSettings] = useState({ displayTimezone: 'Asia/Kolkata' });
  const [selectedAircraft, setSelectedAircraft] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [mainTab, setMainTab] = useState(0); // 0: Dashboard, 1: Aircraft Usage, 2: Assemblies, 3: Components
  const [liveUsageByAircraftId, setLiveUsageByAircraftId] = useState({});
  const [liveUsageLoadingByAircraftId, setLiveUsageLoadingByAircraftId] = useState({});
  const [liveUsageErrorByAircraftId, setLiveUsageErrorByAircraftId] = useState({});
  const [assemblyViewMode, setAssemblyViewMode] = useState(true);
  const [componentViewMode, setComponentViewMode] = useState(true);
  const [componentTableSearchTerm, setComponentTableSearchTerm] = useState('');
  const { user } = useAuth();
  const { showToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const processedDeepLinkRef = useRef(null);
  const displayTimezone = clientSettings.displayTimezone || 'Asia/Kolkata';

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get('/api/client/settings');
        if (res.data?.displayTimezone) {
          setClientSettings((s) => ({ ...s, displayTimezone: res.data.displayTimezone }));
        }
      } catch {
        // keep defaults
      }
    };
    fetchSettings();
  }, []);

  const fetchLiveUsageForAircraft = useCallback(async (aircraftId) => {
    if (!aircraftId) return;
    setLiveUsageLoadingByAircraftId((prev) => ({ ...prev, [aircraftId]: true }));
    setLiveUsageErrorByAircraftId((prev) => ({ ...prev, [aircraftId]: null }));
    try {
      const response = await axios.get(`/api/aircraft/${aircraftId}/live-usage`);
      setLiveUsageByAircraftId((prev) => ({ ...prev, [aircraftId]: response.data || null }));
    } catch (error) {
      console.error('Error fetching aircraft live usage:', error);
      setLiveUsageByAircraftId((prev) => ({ ...prev, [aircraftId]: null }));
      setLiveUsageErrorByAircraftId((prev) => ({
        ...prev,
        [aircraftId]: error?.response?.data?.message || 'Unable to fetch live usage'
      }));
    } finally {
      setLiveUsageLoadingByAircraftId((prev) => ({ ...prev, [aircraftId]: false }));
    }
  }, []);

  useEffect(() => {
    if (mainTab === 0 || !selectedAircraft?._id) return;
    fetchLiveUsageForAircraft(selectedAircraft._id);
  }, [mainTab, selectedAircraft?._id, fetchLiveUsageForAircraft]);

  // Data fetching hook
  const {
    aircraft,
    assemblies,
    components,
    loading,
    dropdownOptions,
    fetchAssemblies,
    fetchComponents,
    fetchDropdownOptions
  } = useAircraftLifecycleData(selectedAircraft, showArchived);

  // Assembly handlers hook
  const {
    open,
    selectedAssembly,
    formData,
    setFormData,
    setOpen,
    handleAddAssembly,
    handleEditAssembly,
    handleSubmitAssembly,
    handleSubmitForApproval,
    handleApprove,
    handleReject,
    handleDeleteAssembly
  } = useAssemblyHandlers(selectedAircraft, dropdownOptions, fetchAssemblies);

  // Component handlers hook
  const {
    componentFormOpen,
    selectedComponent,
    componentFormData,
    setComponentFormData,
    setComponentFormOpen,
    setSelectedComponent,
    handleAddComponent,
    handleEditComponent,
    handleCloneComponent,
    handleSubmitComponent,
    handleDeleteComponent,
    handleAddComponentToInventory,
    handleBackfillComponentsInventory
  } = useComponentHandlers(selectedAircraft, assemblies, fetchComponents);

  // Document handlers hook
  const {
    documentDialogOpen,
    documentContext,
    documentForm,
    documentUploading,
    documentUploadProgress,
    documentList,
    setDocumentDialogOpen,
    setDocumentForm,
    openDocumentDialog,
    handleDocumentUpload,
    handleDocumentDownload
  } = useDocumentHandlers();

  // CSV export hook
  const liveUsagePayload = selectedAircraft ? liveUsageByAircraftId[selectedAircraft._id] : null;
  const { handleExportToCSV, exporting } = useCSVExport(
    selectedAircraft,
    showArchived,
    liveUsagePayload?.currentUsageTotals || null,
    liveUsagePayload?.effectiveTlbUsageMapping ?? null
  );

  const canEdit = [ROLES.SUPER_ADMIN, ROLES.ACCOUNTABLE_MANAGER, ROLES.CA_MANAGER, ROLES.CA_TECHNICAL_OFFICER, ROLES.CAMO_QM_MANAGER, ROLES.CAMO_QM_OFFICER, ROLES.PLANNING_MANAGER, ROLES.MRO_MANAGER, ROLES.MRO_ENGINEER].includes(user?.role);
  const canManageInventory = [ROLES.SUPER_ADMIN, ROLES.ACCOUNTABLE_MANAGER, ROLES.STORE_INSPECTOR, ROLES.STORE_EXECUTIVE].includes(user?.role);
  const canExportAircraftCsv = user?.role === ROLES.SUPER_ADMIN;
  const canApproveAction = canApprove(user?.role);
  const handleExportAllAircraftDataToCSV = useCallback(() => {
    handleExportToCSV({ includeArchived: true });
  }, [handleExportToCSV]);

  const handleAddAssemblyClick = () => {
    setAssemblyViewMode(false);
    handleAddAssembly();
  };

  const handleEditAssemblyClick = (assembly) => {
    setAssemblyViewMode(false);
    handleEditAssembly(assembly);
  };

  const handleViewAssembly = (assembly) => {
    setAssemblyViewMode(true);
    handleEditAssembly(assembly);
  };

  const handleSwitchAssemblyToEdit = () => {
    setAssemblyViewMode(false);
  };

  const handleAddComponentClick = () => {
    setComponentViewMode(false);
    handleAddComponent();
  };

  const handleEditComponentClick = (component) => {
    setComponentViewMode(false);
    handleEditComponent(component);
  };

  const handleViewComponent = (component) => {
    setComponentViewMode(true);
    handleEditComponent(component);
  };

  const handleSwitchComponentToEdit = () => {
    setComponentViewMode(false);
  };

  const handlePartNumberSearchSelect = useCallback(({ aircraft: matchedAircraft, partNumber }) => {
    setSelectedAircraft(matchedAircraft);
    setMainTab(3);
    setComponentTableSearchTerm(partNumber || '');
  }, []);

  const clearComponentDeepLinkParams = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      ['aircraftId', 'componentId', 'cloneFrom', 'partNumber', 'serialNumber', 'workOrderNumber'].forEach((key) => {
        next.delete(key);
      });
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Deep link: /aircraft-lifecycle?aircraftId=…&componentId=… or &cloneFrom=… (MRO workbench, maintenance)
  useEffect(() => {
    const aircraftId = searchParams.get('aircraftId');
    const componentId = searchParams.get('componentId');
    const cloneFrom = searchParams.get('cloneFrom');
    if (!aircraftId || (!componentId && !cloneFrom)) {
      processedDeepLinkRef.current = null;
      return;
    }

    const linkKey = `${aircraftId}:${componentId || ''}:${cloneFrom || ''}`;
    if (processedDeepLinkRef.current === linkKey) return;
    if (!aircraft.length) return;

    processedDeepLinkRef.current = linkKey;
    let cancelled = false;

    (async () => {
      try {
        const ac = aircraft.find((a) => String(a._id) === String(aircraftId));
        if (!ac) {
          showToast('Aircraft not found for component link', 'error');
          clearComponentDeepLinkParams();
          return;
        }
        if (cancelled) return;

        setSelectedAircraft(ac);
        setMainTab(3);

        const targetId = componentId || cloneFrom;
        const res = await axios.get(`/api/components/${targetId}`);
        const component = res.data;
        if (cancelled || !component) return;

        if (componentId) {
          setComponentViewMode(true);
          await handleEditComponent(component);
        } else {
          const overrides = {};
          const partNumber = searchParams.get('partNumber');
          const serialNumber = searchParams.get('serialNumber');
          const workOrderNumber = searchParams.get('workOrderNumber');
          if (partNumber) overrides.partNumber = partNumber;
          if (serialNumber) overrides.serialNumber = serialNumber;
          if (workOrderNumber) overrides.workOrderNumber = workOrderNumber;
          setComponentViewMode(false);
          await handleCloneComponent(component, overrides);
        }

        clearComponentDeepLinkParams();
      } catch (err) {
        console.error('Component deep link failed:', err);
        if (!cancelled) {
          showToast(err.response?.data?.message || 'Could not open component from link', 'error');
          clearComponentDeepLinkParams();
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    searchParams,
    aircraft,
    handleEditComponent,
    handleCloneComponent,
    showToast,
    clearComponentDeepLinkParams
  ]);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Aircraft Life-cycle Management
      </Typography>
      <WorkflowGuide variant="aircraftLifecycle" />

      <PartNumberSearchBar
        aircraft={aircraft}
        onSelectResult={handlePartNumberSearchSelect}
      />

      <Tabs value={mainTab} onChange={(e, newValue) => setMainTab(newValue)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tab icon={<Dashboard />} iconPosition="start" label="Dashboard" />
        <Tab icon={<QueryStats />} iconPosition="start" label="Aircraft Usage" />
        <Tab label="Assemblies" />
        <Tab label="Components" />
      </Tabs>

      {mainTab === 0 && (
        <AircraftLifecycleDashboard />
      )}

      {mainTab >= 1 && (
        <>
      <AircraftSelector
        aircraft={aircraft}
        selectedAircraft={selectedAircraft}
        onSelectAircraft={setSelectedAircraft}
      />

      {selectedAircraft && (
        <>
          {(mainTab === 2 || mainTab === 3) && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, mt: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showArchived}
                    onChange={(e) => setShowArchived(e.target.checked)}
                    size="small"
                  />
                }
                label="Show Archived"
              />
              {mainTab === 2 && (
                <Button
                  variant="outlined"
                  startIcon={<Download />}
                  onClick={() => handleExportToCSV({ target: 'assemblies' })}
                  disabled={exporting}
                >
                  Download Assemblies CSV
                </Button>
              )}
              {mainTab === 3 && (
                <Button
                  variant="outlined"
                  startIcon={<Download />}
                  onClick={() => handleExportToCSV({ target: 'components' })}
                  disabled={exporting}
                >
                  Download Components CSV
                </Button>
              )}
            </Box>
          )}

          {mainTab === 1 && (
            <Box sx={{ mt: 2 }}>
              <LiveAircraftUsagePanel
                liveUsage={liveUsageByAircraftId[selectedAircraft._id] ?? null}
                liveUsageLoading={!!liveUsageLoadingByAircraftId[selectedAircraft._id]}
                liveUsageError={liveUsageErrorByAircraftId[selectedAircraft._id] || null}
                displayTimezone={displayTimezone}
                onRefresh={() => fetchLiveUsageForAircraft(selectedAircraft._id)}
                infoAlert={
                  <Alert severity="info" variant="outlined" sx={{ borderRadius: 1 }}>
                    <Typography variant="body2">
                      Read-only information. Same figures as <strong>Live/Current/Total Aircraft Usage</strong> on the
                      Aircraft record (Warranty &amp; TSN tab), sourced from the latest approved Technical Log Book
                      entry. Displayed as shown on{' '}
                      <Link
                        href="https://app.fleetwright.com/aircraft"
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                      >
                        app.fleetwright.com/aircraft
                      </Link>
                      .
                    </Typography>
                  </Alert>
                }
              />
            </Box>
          )}

          {mainTab === 2 && (
            <AssemblyTable
              selectedAircraft={selectedAircraft}
              assemblies={assemblies}
              loading={loading}
              canEdit={canEdit}
              canApproveAction={canApproveAction}
              getStatusColor={getStatusColor}
              onAddAssembly={handleAddAssemblyClick}
              onEditAssembly={handleEditAssemblyClick}
              onRowClick={handleViewAssembly}
              onSubmitForApproval={handleSubmitForApproval}
              onApprove={handleApprove}
              onReject={handleReject}
              onDeleteAssembly={handleDeleteAssembly}
              onOpenDocumentDialog={openDocumentDialog}
            />
          )}

          {mainTab === 3 && (
            assemblies.length > 0 ? (
              <ComponentTable
                selectedAircraft={selectedAircraft}
                components={components}
                assemblies={assemblies}
                canEdit={canEdit}
                getStatusColor={getStatusColor}
                currentUsageTotals={liveUsagePayload?.currentUsageTotals || null}
                effectiveTlbUsageMapping={liveUsagePayload?.effectiveTlbUsageMapping ?? null}
                initialSearchTerm={componentTableSearchTerm}
                onOpenDocumentDialog={openDocumentDialog}
                onAddComponent={handleAddComponentClick}
                onEditComponent={handleEditComponentClick}
                onRowClick={handleViewComponent}
                onDeleteComponent={handleDeleteComponent}
                onAddToInventory={canManageInventory ? handleAddComponentToInventory : undefined}
                onBackfillInventory={canManageInventory ? handleBackfillComponentsInventory : undefined}
              />
            ) : (
              <Paper sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  Add at least one assembly to manage components for this aircraft.
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Switch to the Assemblies tab and use &quot;Add Assembly&quot; to get started.
                </Typography>
              </Paper>
            )
          )}
        </>
      )}
        </>
      )}

      <AssemblyFormDialog
        open={open}
        onClose={() => {
          setOpen(false);
          setAssemblyViewMode(true);
        }}
        selectedAssembly={selectedAssembly}
        selectedAircraft={selectedAircraft}
        formData={formData}
        setFormData={setFormData}
        dropdownOptions={dropdownOptions}
        onSubmit={handleSubmitAssembly}
        onRefreshDropdowns={fetchDropdownOptions}
        readOnly={assemblyViewMode}
        onSwitchToEdit={handleSwitchAssemblyToEdit}
        assemblies={assemblies}
        currentUsageTotals={liveUsagePayload?.currentUsageTotals || null}
        effectiveTlbUsageMapping={liveUsagePayload?.effectiveTlbUsageMapping ?? null}
        onExportAircraftCsv={handleExportAllAircraftDataToCSV}
        exporting={exporting}
        canExportAircraftCsv={canExportAircraftCsv}
      />

      <DocumentManagementDialog
        open={documentDialogOpen}
        onClose={() => setDocumentDialogOpen(false)}
        documentContext={documentContext}
        documentList={documentList}
        documentForm={documentForm}
        setDocumentForm={setDocumentForm}
        documentUploading={documentUploading}
        documentUploadProgress={documentUploadProgress}
        onUpload={handleDocumentUpload}
        onDownload={handleDocumentDownload}
      />

      <ComponentForm
        open={componentFormOpen}
        onClose={() => {
          setComponentFormOpen(false);
          setSelectedComponent(null);
          setComponentViewMode(true);
        }}
        selectedAircraft={selectedAircraft}
        selectedComponent={selectedComponent}
        formData={componentFormData}
        setFormData={setComponentFormData}
        dropdownOptions={dropdownOptions}
        onSubmit={handleSubmitComponent}
        user={user}
        assemblies={assemblies}
        onRefreshDropdowns={fetchDropdownOptions}
        onOpenAssemblyForm={handleAddAssemblyClick}
        readOnly={componentViewMode}
        onSwitchToEdit={handleSwitchComponentToEdit}
        currentUsageTotals={liveUsagePayload?.currentUsageTotals || null}
        effectiveTlbUsageMapping={liveUsagePayload?.effectiveTlbUsageMapping ?? null}
        onExportAircraftCsv={handleExportAllAircraftDataToCSV}
        exporting={exporting}
        canExportAircraftCsv={canExportAircraftCsv}
      />
    </Box>
  );
};

export default AircraftLifecycle;
